/**
 * Browser - DDD-structured semantic navigation component.
 *
 * Navigation levels follow the DDD hierarchy:
 * - Domains (organize problem space)
 * - Bounded Contexts (semantic authority)
 * - Model (contextual model with layers)
 * - Datasets (knowledge graphs / ABox)
 * - Diagrams (views)
 *
 * Note: Workspace is NOT a navigation level - it's the implementation
 * of a Bounded Context and is accessed internally.
 */

import React from "react";
import clsx from "clsx";
import type { Element, Node, Domain } from "@semantical/modules/semantical-core";
import { isNode, isEdge } from "@semantical/modules/semantical-core";
import type KnowledgeGraph from "@semantical/modules/knowledge-graph/KnowledgeGraph";
import { Individual } from "@semantical/modules/knowledge-graph";
import type { SchemaIndividual } from "@semantical/modules/ontology-model";
import type { Workspace } from "@semantical/modules/workspace";
import NameEditor from "@semantical/components/diagram-editor/NameEditor";
import ContextMenu from "@semantical/components/common/ContextMenu";
import BrowserSection from "./BrowserSection";
import BrowserBreadcrumb from "./BrowserBreadcrumb";
import type {
    NavigationLevel,
    NavigationPath,
    RenameState,
    ContextMenuState,
    BreadcrumbSegment
} from "./types";

/**
 * Props for the Browser component.
 * Supports both legacy props (knowledgeGraph, schemaIndividuals) and new navigation.
 */
type Props = {
    // DDD navigation - full domain hierarchy and active bounded context
    rootDomains?: Domain[];
    boundedContext?: Workspace;

    // Legacy props (preserved for backward compatibility)
    knowledgeGraph: KnowledgeGraph | null;
    schemaIndividuals: SchemaIndividual[];
    selectedElement: Element | null;
    onElementSelected: (element: Element | null) => void;
    onElementHovered: (element: Element | null) => void;
    onElementRenamed?: (element: Node, newName: string) => void;
    onAddSchemaIndividualToDiagram?: (schemaIndividual: SchemaIndividual) => void;
    editingMode?: 'TBox' | 'ABox';
    darkMode?: boolean;

    // Browser panel width (controlled by App)
    browserWidth?: number;
    onBrowserWidthChange?: (width: number) => void;

    // Diagram navigation
    activeDiagramId?: string;
    onDiagramSelected?: (diagramId: string) => void;

    // Dataset (KnowledgeGraph) creation - contextId is the BC where it should be created
    onCreateDataset?: (name: string, contextId: string) => void;

    // Diagram creation - contextId is the BC where it should be created
    onCreateDiagram?: (name: string, contextId: string) => void;

    // New navigation props (optional for now)
    onNavigationChange?: (level: NavigationLevel, path: NavigationPath) => void;

    // Vocabulary selection - triggers concept scheme view
    onVocabularySelected?: (vocabularyUri: string, vocabularyLabel: string) => void;
    selectedVocabularyUri?: string | null;
};

type State = {
    expanded: boolean;

    // Panel resizing (isResizing tracks drag state; width is controlled by props)
    isResizing: boolean;

    // DDD hierarchy expansion - track expanded state by domain/context ID
    // These use Sets because we need programmatic control (e.g., expand path to active context)
    expandedDomains: Set<string>;
    expandedDomainOntologies: Set<string>;
    expandedContexts: Set<string>;

    // DDD navigation state
    focusedLevel: NavigationLevel;
    navigationPath: NavigationPath;

    // Existing functionality
    renameState: RenameState;
    contextMenu: ContextMenuState;
};

// Icons as components for reuse
const DomainIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 3v18M3 12h18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <ellipse cx="12" cy="12" rx="9" ry="4" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
);

const ContextIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const ModelIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const IndividualsIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/>
        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const RelationshipsIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M4 12h16M12 4v16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="4" cy="12" r="2" fill="currentColor"/>
        <circle cx="20" cy="12" r="2" fill="currentColor"/>
    </svg>
);

const ContextualModelIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
);

const SemanticLayerIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="8" cy="6" r="2" fill="currentColor"/>
        <circle cx="12" cy="12" r="2" fill="currentColor"/>
        <circle cx="16" cy="18" r="2" fill="currentColor"/>
    </svg>
);

const DatasetsIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <ellipse cx="12" cy="6" rx="8" ry="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M4 6v12c0 1.66 3.58 3 8 3s8-1.34 8-3V6" stroke="currentColor" strokeWidth="2"/>
        <path d="M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3" stroke="currentColor" strokeWidth="2"/>
    </svg>
);

const DiagramsIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        <path d="M14 17.5h7M17.5 14v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const KnowledgeGraphIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="6" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
        <circle cx="18" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
        <circle cx="12" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M8.5 7.5L10.5 16M15.5 7.5L13.5 16M9 6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const DomainOntologiesIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M12 2L3 7v10l9 5 9-5V7l-9-5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        <path d="M12 12l9-5M12 12v10M12 12L3 7" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    </svg>
);

const OntologyIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="5" r="3" stroke="currentColor" strokeWidth="2"/>
        <circle cx="5" cy="19" r="3" stroke="currentColor" strokeWidth="2"/>
        <circle cx="19" cy="19" r="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M12 8v4M9 14l-2.5 3M15 14l2.5 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

const UbiquitousLanguageIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M7 8h10M7 12h10M7 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="17" cy="16" r="2" fill="currentColor"/>
    </svg>
);

const VocabularyIcon = ({ size = 14 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M8 6h8M8 10h8M8 14h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

// Alias for singular form
const IndividualIcon = IndividualsIcon;
const RelationshipIcon = RelationshipsIcon;

export default class Browser extends React.Component<Props, State> {
    state: State = {
        expanded: true,
        // Panel resizing (width controlled by props)
        isResizing: false,
        // DDD hierarchy expansion - expanded by default to show active context
        expandedDomains: new Set<string>(),
        expandedDomainOntologies: new Set<string>(),
        expandedContexts: new Set<string>(),
        focusedLevel: 'bounded-contexts',
        navigationPath: {},
        renameState: {
            visible: false,
            element: null,
            position: { x: 0, y: 0 }
        },
        contextMenu: {
            visible: false,
            x: 0,
            y: 0,
            element: null
        }
    };

    componentDidMount() {
        document.addEventListener('keydown', this.handleKeyDown);
        // Expand all domains and contexts by default
        this.expandAllDomainsAndContexts();
    }

    /**
     * Expand all domains and contexts in the hierarchy by default.
     */
    expandAllDomainsAndContexts = () => {
        const { rootDomains, boundedContext } = this.props;
        if (!rootDomains) return;

        const expandedDomains = new Set<string>();
        const expandedDomainOntologies = new Set<string>();
        const expandedContexts = new Set<string>();

        const collectDomainIds = (domains: Domain[]) => {
            for (const domain of domains) {
                expandedDomains.add(domain.id);
                expandedDomainOntologies.add(domain.id);
                // Expand children recursively
                collectDomainIds(domain.getChildren());
            }
        };

        collectDomainIds(rootDomains);

        // Expand the active bounded context
        if (boundedContext) {
            expandedContexts.add(boundedContext.id);
        }

        this.setState({ expandedDomains, expandedDomainOntologies, expandedContexts });
    };

    componentDidUpdate(prevProps: Props) {
        // When active diagram changes, ensure the parent context is expanded
        if (this.props.activeDiagramId && this.props.activeDiagramId !== prevProps.activeDiagramId) {
            this.expandContextWithActiveDiagram();
        }

        // When bounded context changes, expand it
        if (this.props.boundedContext && prevProps.boundedContext &&
            this.props.boundedContext.id !== prevProps.boundedContext.id) {
            this.setState(prev => {
                const newSet = new Set(prev.expandedContexts);
                newSet.add(this.props.boundedContext!.id);
                return { expandedContexts: newSet };
            });
        }
    }

    /**
     * Expand the context containing the active diagram.
     */
    expandContextWithActiveDiagram = () => {
        const { rootDomains, activeDiagramId } = this.props;
        if (!rootDomains || !activeDiagramId) return;

        // Find which context contains the active diagram
        const findContextWithDiagram = (domains: Domain[]): string | null => {
            for (const domain of domains) {
                // Check bounded contexts in this domain
                for (const context of domain.getBoundedContexts()) {
                    const views = context.getViews();
                    if (views.some(v => v.id === activeDiagramId)) {
                        return context.id;
                    }
                }
                // Check child domains
                const result = findContextWithDiagram(domain.getChildren());
                if (result) return result;
            }
            return null;
        };

        const contextId = findContextWithDiagram(rootDomains);
        if (contextId && !this.state.expandedContexts.has(contextId)) {
            this.setState(prev => {
                const newSet = new Set(prev.expandedContexts);
                newSet.add(contextId);
                return { expandedContexts: newSet };
            });
        }
    };

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        // Clean up resize listeners if component unmounts during resize
        document.removeEventListener('mousemove', this.handleResizeMove);
        document.removeEventListener('mouseup', this.handleResizeEnd);
    }

    private browserRef = React.createRef<HTMLDivElement>();

    // ==================== Keyboard Handling ====================

    handleKeyDown = (event: KeyboardEvent) => {
        // Ignore if typing in an input field
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return;
        }

        // Only handle F2 if Browser panel contains the focus or was recently clicked
        const browserEl = this.browserRef.current;
        if (!browserEl) return;

        const hasFocus = browserEl.contains(document.activeElement) || browserEl === document.activeElement;
        if (!hasFocus) {
            return;
        }

        // Handle F2 to rename selected element
        if (event.key === 'F2' && this.props.selectedElement && isNode(this.props.selectedElement)) {
            event.preventDefault();
            this.initiateRename();
        }
    };

    // ==================== Rename Functionality ====================

    /**
     * Initiate renaming the selected element via F2 key.
     * Mode-aware: In ABox mode, only Individuals can be renamed.
     * In TBox mode, only SchemaIndividuals can be renamed.
     */
    initiateRename = () => {
        const { selectedElement, editingMode = 'ABox' } = this.props;

        if (!selectedElement || !isNode(selectedElement)) {
            return;
        }

        // Mode-aware validation
        const isIndividual = selectedElement instanceof Individual;

        if (editingMode === 'ABox' && !isIndividual) {
            console.debug("In ABox mode, only Individuals can be renamed");
            return;
        }
        if (editingMode === 'TBox' && isIndividual) {
            console.debug("In TBox mode, Individuals cannot be renamed");
            return;
        }

        // Find the DOM element for the selected item to get position
        const itemElement = document.querySelector(`[data-element-uri="${selectedElement.uri}"]`);
        if (!itemElement) {
            console.debug("Could not find DOM element for positioning");
            return;
        }

        const rect = itemElement.getBoundingClientRect();
        const screenX = rect.left + rect.width / 2;
        const screenY = rect.top + rect.height / 2;

        this.setState({
            renameState: {
                visible: true,
                element: selectedElement,
                position: { x: screenX, y: screenY }
            }
        });
    };

    handleRenameConfirm = (newName: string) => {
        const { renameState } = this.state;
        const trimmedName = newName.trim();

        if (renameState.element && trimmedName && trimmedName !== renameState.element.name) {
            this.props.onElementRenamed?.(renameState.element, trimmedName);
        }

        this.hideRename();
    };

    handleRenameCancel = () => {
        this.hideRename();
    };

    hideRename = () => {
        this.setState({
            renameState: {
                visible: false,
                element: null,
                position: { x: 0, y: 0 }
            }
        });
    };

    // ==================== Toggle Handlers ====================

    handleToggleExpand = () => {
        this.setState(prev => ({ expanded: !prev.expanded }));
    };

    handleToggleDomain = (domainId: string) => {
        this.setState(prev => {
            const newSet = new Set(prev.expandedDomains);
            if (newSet.has(domainId)) {
                newSet.delete(domainId);
            } else {
                newSet.add(domainId);
            }
            return { expandedDomains: newSet };
        });
    };

    handleToggleDomainOntologies = (domainId: string) => {
        this.setState(prev => {
            const newSet = new Set(prev.expandedDomainOntologies);
            if (newSet.has(domainId)) {
                newSet.delete(domainId);
            } else {
                newSet.add(domainId);
            }
            return { expandedDomainOntologies: newSet };
        });
    };

    handleToggleContext = (contextId: string) => {
        this.setState(prev => {
            const newSet = new Set(prev.expandedContexts);
            if (newSet.has(contextId)) {
                newSet.delete(contextId);
            } else {
                newSet.add(contextId);
            }
            return { expandedContexts: newSet };
        });
    };

    // ==================== Panel Resizing ====================

    handleResizeStart = (e: React.MouseEvent) => {
        e.preventDefault();
        this.setState({ isResizing: true });
        document.addEventListener('mousemove', this.handleResizeMove);
        document.addEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
    };

    handleResizeMove = (e: MouseEvent) => {
        if (!this.state.isResizing) return;
        const newWidth = e.clientX;
        // Constrain width between min and max
        const minWidth = 180;
        const maxWidth = 400;
        const constrainedWidth = Math.min(Math.max(newWidth, minWidth), maxWidth);
        // Call callback to update width in App state
        this.props.onBrowserWidthChange?.(constrainedWidth);
    };

    handleResizeEnd = () => {
        this.setState({ isResizing: false });
        document.removeEventListener('mousemove', this.handleResizeMove);
        document.removeEventListener('mouseup', this.handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    };

    // ==================== Dataset & Diagram Creation ====================

    handleCreateDataset = (e: React.MouseEvent, contextId: string) => {
        e.preventDefault();
        e.stopPropagation();

        // Expand the parent <details> element before prompting
        const detailsEl = (e.currentTarget as HTMLElement).closest('details');
        if (detailsEl) {
            detailsEl.open = true;
        }

        const name = window.prompt('Enter name for new Knowledge Graph:', 'New Knowledge Graph');
        if (name && name.trim()) {
            this.props.onCreateDataset?.(name.trim(), contextId);
        }
    };

    handleCreateDiagram = (e: React.MouseEvent, contextId: string) => {
        e.preventDefault();
        e.stopPropagation();

        // Expand the parent <details> element before prompting
        const detailsEl = (e.currentTarget as HTMLElement).closest('details');
        if (detailsEl) {
            detailsEl.open = true;
        }

        const name = window.prompt('Enter name for new Diagram:', 'New Diagram');
        if (name && name.trim()) {
            this.props.onCreateDiagram?.(name.trim(), contextId);
        }
    };

    // ==================== Element Interaction ====================

    handleElementClick = (element: Element) => {
        this.props.onElementSelected(element);
    };

    handleElementHover = (element: Element | null) => {
        this.props.onElementHovered(element);
    };

    handleSchemaIndividualContextMenu = (e: React.MouseEvent, schemaIndividual: SchemaIndividual) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({
            contextMenu: {
                visible: true,
                x: e.clientX,
                y: e.clientY,
                element: schemaIndividual
            }
        });
    };

    handleContextMenuSelect = (action: string) => {
        const { contextMenu } = this.state;
        if (action === 'add-to-diagram' && contextMenu.element) {
            this.props.onAddSchemaIndividualToDiagram?.(contextMenu.element);
        }
        this.hideContextMenu();
    };

    hideContextMenu = () => {
        this.setState({
            contextMenu: { visible: false, x: 0, y: 0, element: null }
        });
    };

    // ==================== Navigation ====================

    handleNavigate = (level: NavigationLevel, path: NavigationPath) => {
        this.setState({
            focusedLevel: level,
            navigationPath: path
        });
        this.props.onNavigationChange?.(level, path);
    };

    /**
     * Build breadcrumb segments from current navigation state.
     * Format: Domain > Context > Model (no Workspace shown)
     */
    getBreadcrumbSegments(): BreadcrumbSegment[] {
        const { focusedLevel } = this.state;
        const segments: BreadcrumbSegment[] = [];

        // For now, show a simple context indicator
        // This will be expanded as Domain/BoundedContext are fully implemented
        if (focusedLevel !== 'domains') {
            segments.push({
                key: 'context',
                label: 'Context',
                level: 'bounded-contexts',
                path: {}
            });
        }

        return segments;
    }

    // ==================== Recursive Domain Rendering ====================

    /**
     * Render a domain and its children recursively.
     */
    renderDomain = (domain: Domain, depth: number = 0): React.ReactNode => {
        const {
            expandedDomains,
            expandedDomainOntologies,
            expandedContexts
        } = this.state;

        const isDomainExpanded = expandedDomains.has(domain.id);
        const isDomainOntologiesExpanded = expandedDomainOntologies.has(domain.id);
        const children = domain.getChildren();
        const boundedContexts = domain.getBoundedContexts();
        const ontologies = domain.getOntologies();
        const knowledgeGraph = this.props.knowledgeGraph;

        return (
            <div key={domain.id}>
                {/* Domain header */}
                <button
                    className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                    onClick={() => this.handleToggleDomain(domain.id)}
                >
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        className={clsx(
                            "transition-transform duration-150 text-slate-400 dark:text-white/40",
                            isDomainExpanded && "rotate-90"
                        )}
                    >
                        <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <span className="text-blue-500 dark:text-blue-400">
                        <DomainIcon />
                    </span>
                    <span className="flex-1 text-xs font-medium">{domain.name}</span>
                </button>

                {/* Domain children */}
                {isDomainExpanded && (
                    <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                        {/* Domain Ontologies */}
                        {ontologies.length > 0 && (
                            <div>
                                <button
                                    className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                                    onClick={() => this.handleToggleDomainOntologies(domain.id)}
                                >
                                    <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        className={clsx(
                                            "transition-transform duration-150 text-slate-400 dark:text-white/40",
                                            isDomainOntologiesExpanded && "rotate-90"
                                        )}
                                    >
                                        <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                    <span className="text-amber-500 dark:text-amber-400">
                                        <DomainOntologiesIcon />
                                    </span>
                                    <span className="flex-1 text-xs font-medium">Domain Ontologies</span>
                                </button>

                                {isDomainOntologiesExpanded && (
                                    <div className="ml-3 border-l border-slate-200 dark:border-white/10 pb-1">
                                        {ontologies.map(ontologyRef => (
                                            <div key={ontologyRef.id} className="flex items-center gap-2 px-2.5 py-1.5 mx-1.5">
                                                <span className="text-violet-500 dark:text-violet-400">
                                                    <OntologyIcon />
                                                </span>
                                                <span className="text-xs font-medium">{ontologyRef.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Subdomains (children) */}
                        {children.map(child => this.renderDomain(child, depth + 1))}

                        {/* Bounded Contexts in this domain */}
                        {boundedContexts.map(context => {
                            const isContextExpanded = expandedContexts.has(context.id);
                            // Get model and UL from the context itself, not just the active workspace
                            const contextModel = context.getModel?.();
                            const contextUL = context.getUbiquitousLanguage?.();
                            const contextViews = context.getViews();
                            // Check if this context contains the active diagram
                            const hasActiveDiagram = contextViews.some(v => v.id === this.props.activeDiagramId);

                            // Get this context's semantic layer ontology references (configuration, not loaded schemas)
                            const contextImportedOntologies = contextModel?.getImportedOntologies() ?? [];
                            const contextLocalOntologies = contextModel?.getLocalOntologies() ?? [];

                            return (
                                <div key={context.id}>
                                    <div className="flex items-center gap-2 w-full px-2.5 py-2 hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors">
                                        {/* Chevron - toggles expand/collapse */}
                                        <button
                                            className="flex-shrink-0 p-0.5 -m-0.5 rounded hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.handleToggleContext(context.id);
                                            }}
                                        >
                                            <svg
                                                width="12"
                                                height="12"
                                                viewBox="0 0 12 12"
                                                className={clsx(
                                                    "transition-transform duration-150 text-slate-400 dark:text-white/40",
                                                    isContextExpanded && "rotate-90"
                                                )}
                                            >
                                                <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                        {/* Context name - click chevron to expand/collapse */}
                                        <div className="flex items-center gap-2 flex-1">
                                            <span className="text-emerald-500 dark:text-emerald-400">
                                                <ContextIcon />
                                            </span>
                                            <span className="flex-1 text-xs font-medium">
                                                {context.name}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Context contents - ALWAYS visible when expanded */}
                                    {isContextExpanded && (
                                        <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                                            {/* Ubiquitous Language - ABOVE Contextual Model */}
                                            {contextUL && (
                                                <details className="group">
                                                    <summary className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                                        <svg
                                                            width="12"
                                                            height="12"
                                                            viewBox="0 0 12 12"
                                                            className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open:rotate-90"
                                                        >
                                                            <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                        </svg>
                                                        <span className="text-amber-500 dark:text-amber-400">
                                                            <UbiquitousLanguageIcon />
                                                        </span>
                                                        <span className="flex-1 text-xs font-medium">Ubiquitous Language</span>
                                                    </summary>

                                                    <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                                                        {/* Imported Vocabularies */}
                                                        {contextUL.getImportedVocabularies().map(vocabRef => {
                                                            const isSelected = this.props.selectedVocabularyUri === vocabRef.uri;
                                                            return (
                                                                <div
                                                                    key={vocabRef.uri}
                                                                    onClick={() => {
                                                                        this.props.onElementSelected(null); // Clear element selection
                                                                        this.props.onVocabularySelected?.(vocabRef.uri, vocabRef.label);
                                                                    }}
                                                                    className={clsx(
                                                                        "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded cursor-pointer",
                                                                        isSelected
                                                                            ? "bg-accent-teal/10 dark:bg-accent/20"
                                                                            : "hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                                                                    )}
                                                                >
                                                                    <span className={isSelected ? "text-accent-teal dark:text-accent" : "text-slate-400 dark:text-white/40"}>
                                                                        <VocabularyIcon size={12} />
                                                                    </span>
                                                                    <span className={clsx("flex-1 text-xs", isSelected && "text-accent-teal dark:text-accent font-medium")}>{vocabRef.label}</span>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                                                                        imported
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}

                                                        {/* Local Vocabularies */}
                                                        {contextUL.getLocalVocabularies().map(vocabRef => {
                                                            const isSelected = this.props.selectedVocabularyUri === vocabRef.uri;
                                                            return (
                                                                <div
                                                                    key={vocabRef.uri}
                                                                    onClick={() => {
                                                                        this.props.onElementSelected(null); // Clear element selection
                                                                        this.props.onVocabularySelected?.(vocabRef.uri, vocabRef.label);
                                                                    }}
                                                                    className={clsx(
                                                                        "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded cursor-pointer",
                                                                        isSelected
                                                                            ? "bg-accent-teal/10 dark:bg-accent/20"
                                                                            : "hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                                                                    )}
                                                                >
                                                                    <span className={isSelected ? "text-accent-teal dark:text-accent" : "text-slate-400 dark:text-white/40"}>
                                                                        <VocabularyIcon size={12} />
                                                                    </span>
                                                                    <span className={clsx("flex-1 text-xs", isSelected && "text-accent-teal dark:text-accent font-medium")}>{vocabRef.label}</span>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300">
                                                                        local
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}

                                                        {/* Empty state */}
                                                        {contextUL.getAllVocabularies().length === 0 && (
                                                            <div className="px-2.5 py-2 text-xs text-slate-400 dark:text-white/40 italic">
                                                                No vocabularies defined
                                                            </div>
                                                        )}
                                                    </div>
                                                </details>
                                            )}

                                            {/* Contextual Model */}
                                            <details className="group">
                                                <summary className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open:rotate-90"
                                                    >
                                                        <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                    </svg>
                                                    <span className="text-purple-500 dark:text-purple-400">
                                                        <ContextualModelIcon />
                                                    </span>
                                                    <span className="flex-1 text-xs font-medium">Contextual Model</span>
                                                </summary>

                                                <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                                                    {contextModel && (
                                                        <div className="px-2.5 py-1.5 text-[10px] text-slate-500 dark:text-white/40 font-medium uppercase tracking-wide">
                                                            {contextModel.name}
                                                        </div>
                                                    )}

                                                    {/* Semantic Layer */}
                                                    <details className="group/sl">
                                                        <summary className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                                            <svg
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 12 12"
                                                                className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open/sl:rotate-90"
                                                            >
                                                                <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                            </svg>
                                                            <span className="text-indigo-500 dark:text-indigo-400">
                                                                <SemanticLayerIcon />
                                                            </span>
                                                            <span className="flex-1 text-xs font-medium">Semantic Layer</span>
                                                        </summary>

                                                        <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                                                            {/* Imported Ontologies */}
                                                            {contextImportedOntologies.map(ontologyRef => (
                                                                <div
                                                                    key={ontologyRef.iri}
                                                                    className="flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                                                                >
                                                                    <span className="text-violet-500 dark:text-violet-400">
                                                                        <OntologyIcon />
                                                                    </span>
                                                                    <span className="flex-1 text-xs">{ontologyRef.label}</span>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300">
                                                                        imported
                                                                    </span>
                                                                </div>
                                                            ))}

                                                            {/* Local Ontologies */}
                                                            {contextLocalOntologies.map(ontologyRef => (
                                                                <div
                                                                    key={ontologyRef.iri}
                                                                    className="flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                                                                >
                                                                    <span className="text-violet-500 dark:text-violet-400">
                                                                        <OntologyIcon />
                                                                    </span>
                                                                    <span className="flex-1 text-xs">{ontologyRef.label}</span>
                                                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300">
                                                                        local
                                                                    </span>
                                                                </div>
                                                            ))}

                                                            {/* Empty state */}
                                                            {contextImportedOntologies.length === 0 && contextLocalOntologies.length === 0 && (
                                                                <div className="px-2.5 py-2 text-xs text-slate-400 dark:text-white/40 italic">
                                                                    No ontologies defined
                                                                </div>
                                                            )}
                                                        </div>
                                                    </details>
                                                </div>
                                            </details>

                                            {/* Datasets */}
                                            {(() => {
                                                const contextDatasets = context.getDatasets?.() ?? [];
                                                return (
                                                    <details className="group">
                                                        <summary className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                                            <svg
                                                                width="12"
                                                                height="12"
                                                                viewBox="0 0 12 12"
                                                                className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open:rotate-90"
                                                            >
                                                                <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                            </svg>
                                                            <span className="text-cyan-500 dark:text-cyan-400">
                                                                <DatasetsIcon />
                                                            </span>
                                                            <span className="flex-1 text-xs font-medium">Datasets</span>
                                                            {this.props.onCreateDataset && (
                                                                <button
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-white/10 text-slate-400 dark:text-white/40 hover:text-slate-600 dark:hover:text-white/70"
                                                                    onClick={(e) => this.handleCreateDataset(e, context.id)}
                                                                    title="Add Knowledge Graph"
                                                                >
                                                                    <svg width="12" height="12" viewBox="0 0 12 12">
                                                                        <path d="M6,2 L6,10 M2,6 L10,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                        </summary>

                                                        <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                                                            {contextDatasets.length > 0 ? (
                                                                contextDatasets.map(datasetRef => (
                                                                    <details key={datasetRef.id} className="group/kg">
                                                                        <summary className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                                                            <svg
                                                                                width="12"
                                                                                height="12"
                                                                                viewBox="0 0 12 12"
                                                                                className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open/kg:rotate-90"
                                                                            >
                                                                                <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                                            </svg>
                                                                            <span className="text-teal-500 dark:text-teal-400">
                                                                                <KnowledgeGraphIcon />
                                                                            </span>
                                                                            <span className="flex-1 text-xs font-medium">{datasetRef.name}</span>
                                                                        </summary>

                                                                        {/* Show KG contents if this is the active KG */}
                                                                        {knowledgeGraph && datasetRef.id === knowledgeGraph.id && (
                                                                            <div className="ml-3 border-l border-slate-200 dark:border-white/10">
                                                                                {this.renderKnowledgeGraphContents(knowledgeGraph)}
                                                                            </div>
                                                                        )}
                                                                    </details>
                                                                ))
                                                            ) : (
                                                                <div className="px-3 py-2 text-[11px] text-slate-400 dark:text-white/30 italic">
                                                                    No datasets yet
                                                                </div>
                                                            )}
                                                        </div>
                                                    </details>
                                                );
                                            })()}

                                            {/* Diagrams */}
                                            <details className="group" open={hasActiveDiagram || undefined}>
                                                <summary className="flex items-center gap-2 w-full px-2.5 py-2 text-left hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                                                    <svg
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        className="transition-transform duration-150 text-slate-400 dark:text-white/40 group-open:rotate-90"
                                                    >
                                                        <path d="M4,2 L8,6 L4,10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                    </svg>
                                                    <span className="text-orange-500 dark:text-orange-400">
                                                        <DiagramsIcon />
                                                    </span>
                                                    <span className="flex-1 text-xs font-medium">Diagrams</span>
                                                    {this.props.onCreateDiagram && (
                                                        <button
                                                            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-white/10 text-slate-400 dark:text-white/40 hover:text-slate-600 dark:hover:text-white/70"
                                                            onClick={(e) => this.handleCreateDiagram(e, context.id)}
                                                            title="Add Diagram"
                                                        >
                                                            <svg width="12" height="12" viewBox="0 0 12 12">
                                                                <path d="M6,2 L6,10 M2,6 L10,6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                                            </svg>
                                                        </button>
                                                    )}
                                                </summary>

                                                <div className="ml-3 border-l border-slate-200 dark:border-white/10 pb-2">
                                                    {contextViews.length > 0 ? (
                                                        contextViews.map(view => {
                                                            // Diagram is active only if it matches AND no vocabulary is selected
                                                            const isActive = view.id === this.props.activeDiagramId && !this.props.selectedVocabularyUri;
                                                            return (
                                                                <div
                                                                    key={view.id}
                                                                    className={clsx(
                                                                        "flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors",
                                                                        isActive
                                                                            ? "bg-accent-teal/10 dark:bg-accent/10"
                                                                            : "hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                                                                    )}
                                                                    onClick={() => this.props.onDiagramSelected?.(view.id)}
                                                                >
                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-orange-400 dark:text-orange-300">
                                                                        <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                                                                        <rect x="7" y="7" width="4" height="4" fill="currentColor"/>
                                                                        <rect x="13" y="13" width="4" height="4" fill="currentColor"/>
                                                                        <path d="M9 11L11 13" stroke="currentColor" strokeWidth="1.5"/>
                                                                    </svg>
                                                                    <span className={clsx(
                                                                        "text-xs truncate",
                                                                        isActive && "font-medium text-accent-teal dark:text-accent"
                                                                    )}>
                                                                        {view.name}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div className="px-3 py-2 text-[11px] text-slate-400 dark:text-white/30 italic">
                                                            No diagrams yet
                                                        </div>
                                                    )}
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    /**
     * Render knowledge graph contents (individuals and relationships).
     */
    renderKnowledgeGraphContents = (knowledgeGraph: KnowledgeGraph): React.ReactNode => {
        const { expanded } = this.state;
        const individuals = Array.from(knowledgeGraph.individuals.values());
        const relationships = Array.from(knowledgeGraph.relationships.values());

        return (
            <>
                {/* Individuals */}
                <BrowserSection
                    label="Individuals"
                    icon={<IndividualIcon />}
                    iconColorClass="text-sky-500 dark:text-sky-400"
                    count={individuals.length}
                    panelExpanded={expanded}
                >
                    {individuals.map(ind => this.renderIndividual(ind))}
                </BrowserSection>

                {/* Relationships */}
                <BrowserSection
                    label="Relationships"
                    icon={<RelationshipIcon />}
                    iconColorClass="text-rose-500 dark:text-rose-400"
                    count={relationships.length}
                    panelExpanded={expanded}
                >
                    {relationships.map(rel => this.renderRelationship(rel))}
                </BrowserSection>
            </>
        );
    };

    /**
     * Render a schema individual item (TBox individual from ontology).
     */
    renderSchemaIndividual = (schemaIndividual: SchemaIndividual): React.ReactNode => {
        const { selectedElement } = this.props;

        return (
            <div
                key={schemaIndividual.uri}
                data-element-uri={schemaIndividual.uri}
                className={clsx(
                    "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded-[8px] cursor-pointer transition-all duration-[120ms]",
                    "hover:bg-violet-500/[0.08] dark:hover:bg-violet-400/10",
                    isNode(selectedElement) && selectedElement.uri === schemaIndividual.uri && "bg-violet-500/15 dark:bg-violet-400/[0.18]"
                )}
                onClick={() => this.handleElementClick(schemaIndividual)}
                onContextMenu={(e) => this.handleSchemaIndividualContextMenu(e, schemaIndividual)}
                onMouseEnter={() => this.handleElementHover(schemaIndividual)}
                onMouseLeave={() => this.handleElementHover(null)}
            >
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">
                        {schemaIndividual.name}
                    </div>
                </div>
                {schemaIndividual.classUris.length > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300 whitespace-nowrap">
                        {schemaIndividual.classUris[0].split('#').pop() || schemaIndividual.classUris[0].split('/').pop()}
                    </span>
                )}
            </div>
        );
    };

    /**
     * Render an individual item (ABox individual from knowledge graph).
     */
    renderIndividual = (individual: Individual): React.ReactNode => {
        const { selectedElement } = this.props;

        return (
            <div
                key={individual.uri}
                data-element-uri={individual.uri}
                className={clsx(
                    "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded-[8px] cursor-pointer transition-all duration-[120ms]",
                    "hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10",
                    isNode(selectedElement) && selectedElement.uri === individual.uri && "bg-accent-teal/15 dark:bg-accent/[0.18]"
                )}
                onClick={() => this.handleElementClick(individual)}
                onMouseEnter={() => this.handleElementHover(individual)}
                onMouseLeave={() => this.handleElementHover(null)}
            >
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">
                        {individual.name}
                    </div>
                </div>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/50 whitespace-nowrap">
                    {individual.stereotype}
                </span>
            </div>
        );
    };

    /**
     * Render a relationship item.
     */
    renderRelationship = (relationship: import("@semantical/modules/knowledge-graph").Relationship): React.ReactNode => {
        const { selectedElement } = this.props;

        return (
            <div
                key={relationship.id}
                className={clsx(
                    "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded-[8px] cursor-pointer transition-all duration-[120ms]",
                    "hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10",
                    isEdge(selectedElement) && selectedElement.id === relationship.id && "bg-accent-teal/15 dark:bg-accent/[0.18]"
                )}
                onClick={() => this.handleElementClick(relationship)}
                onMouseEnter={() => this.handleElementHover(relationship)}
                onMouseLeave={() => this.handleElementHover(null)}
            >
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">
                        {relationship.name}
                    </div>
                    <div className="text-[10px] text-slate-400 dark:text-white/40 truncate">
                        {relationship.subject.name} → {relationship.object.name}
                    </div>
                </div>
            </div>
        );
    };

    // ==================== Render ====================

    render() {
        const { boundedContext, rootDomains, knowledgeGraph, schemaIndividuals, selectedElement, browserWidth = 240 } = this.props;
        const {
            expanded,
            isResizing,
            focusedLevel
        } = this.state;

        const individuals = knowledgeGraph
            ? Array.from(knowledgeGraph.individuals.values())
            : [];
        const relationships = knowledgeGraph
            ? Array.from(knowledgeGraph.relationships.values())
            : [];

        const collapsedWidth = 66;

        // Don't render if no rootDomains AND no bounded context AND no knowledge graph AND no schema individuals
        // (rootDomains enables DDD navigation even without an active bounded context)
        if (!rootDomains?.length && !boundedContext && !knowledgeGraph && schemaIndividuals.length === 0) {
            return null;
        }

        const breadcrumbSegments = this.getBreadcrumbSegments();

        return (
            <div
                ref={this.browserRef}
                tabIndex={-1}
                className={clsx(
                    "relative flex flex-col h-full bg-white/[0.98] dark:bg-surface-dark border-r border-slate-200 dark:border-accent-border font-sans text-[13px] text-slate-700 dark:text-[#e0e0e0] outline-none",
                    !isResizing && "transition-[width] duration-200"
                )}
                style={{
                    width: expanded ? browserWidth : collapsedWidth,
                    minWidth: expanded ? browserWidth : collapsedWidth
                }}
            >
                {/* Resize handle - no hover effect, only shows during resize */}
                {expanded && (
                    <div
                        className="absolute top-0 -right-1 w-3 h-full cursor-ew-resize z-10"
                        onMouseDown={this.handleResizeStart}
                    >
                        {/* Visible handle line - only during resize */}
                        <div className={clsx(
                            "absolute top-0 left-1/2 -translate-x-1/2 w-[2px] h-full transition-all duration-150",
                            isResizing ? "bg-accent-teal dark:bg-accent" : "bg-transparent"
                        )} />
                    </div>
                )}
                {/* Header with toggle */}
                <div className="flex items-center gap-2.5 px-2.5 py-3 border-b border-slate-200 dark:border-white/[0.06]">
                    <button
                        className="flex items-center justify-center w-[38px] h-[38px] p-0 bg-accent-teal/10 dark:bg-accent/10 border border-accent-teal/25 dark:border-accent/25 rounded-[10px] cursor-pointer text-accent-teal dark:text-accent transition-all duration-150 hover:bg-accent-teal/20 dark:hover:bg-accent/20"
                        onClick={this.handleToggleExpand}
                        title={expanded ? "Collapse browser" : "Expand browser"}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            {expanded ? (
                                <path d="M11,4 L6,9 L11,14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            ) : (
                                <path d="M7,4 L12,9 L7,14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            )}
                        </svg>
                    </button>
                    {expanded && (
                        <span className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap">Browser</span>
                    )}
                </div>

                {/* Breadcrumb navigation (shown when expanded and navigated deep) */}
                {expanded && breadcrumbSegments.length > 0 && (
                    <BrowserBreadcrumb
                        segments={breadcrumbSegments}
                        currentLevel={focusedLevel}
                        onNavigate={this.handleNavigate}
                        darkMode={this.props.darkMode}
                    />
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden">
                    {/* Domain Hierarchy - Recursive rendering of all root domains */}
                    {expanded && rootDomains && rootDomains.length > 0 && (
                        rootDomains.map(domain => this.renderDomain(domain))
                    )}

                    {/* Fallback: Show sections without domain hierarchy when no rootDomains */}
                    {(!rootDomains || rootDomains.length === 0) && (
                        <>
                            {/* Model Section (TBox Individuals from Ontology) */}
                            {schemaIndividuals.length > 0 && (
                                <BrowserSection
                                    label="Model"
                                    count={schemaIndividuals.length}
                                    panelExpanded={expanded}
                                    icon={<ModelIcon />}
                                    iconColorClass="text-purple-500 dark:text-purple-400"
                                    darkMode={this.props.darkMode}
                                >
                                    {schemaIndividuals.map(schemaIndividual => (
                                        <div
                                            key={schemaIndividual.uri}
                                            data-element-uri={schemaIndividual.uri}
                                            className={clsx(
                                                "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded-[8px] cursor-pointer transition-all duration-[120ms]",
                                                "hover:bg-purple-500/[0.08] dark:hover:bg-purple-400/10",
                                                isNode(selectedElement) && selectedElement.uri === schemaIndividual.uri && "bg-purple-500/15 dark:bg-purple-400/[0.18]"
                                            )}
                                            onClick={() => this.handleElementClick(schemaIndividual)}
                                            onContextMenu={(e) => this.handleSchemaIndividualContextMenu(e, schemaIndividual)}
                                            onMouseEnter={() => this.handleElementHover(schemaIndividual)}
                                            onMouseLeave={() => this.handleElementHover(null)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium truncate">
                                                    {schemaIndividual.name}
                                                </div>
                                            </div>
                                            {schemaIndividual.classUris.length > 0 && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 whitespace-nowrap">
                                                    {schemaIndividual.classUris[0].split('#').pop() || schemaIndividual.classUris[0].split('/').pop()}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </BrowserSection>
                            )}

                            {/* Individuals Section (ABox - Knowledge Graph) */}
                            <BrowserSection
                                label="Individuals"
                                count={individuals.length}
                                panelExpanded={expanded}
                                icon={<IndividualsIcon />}
                                iconColorClass="text-accent-teal dark:text-accent"
                                darkMode={this.props.darkMode}
                            >
                                {individuals.length === 0 ? (
                                    <div className="px-3 py-2 text-[11px] text-slate-400 dark:text-white/30 italic">
                                        No individuals yet
                                    </div>
                                ) : (
                                    individuals.map(individual => (
                                        <div
                                            key={individual.uri}
                                            data-element-uri={individual.uri}
                                            className={clsx(
                                                "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded-[8px] cursor-pointer transition-all duration-[120ms]",
                                                "hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10",
                                                isNode(selectedElement) && selectedElement.uri === individual.uri && "bg-accent-teal/15 dark:bg-accent/[0.18]"
                                            )}
                                            onClick={() => this.handleElementClick(individual)}
                                            onMouseEnter={() => this.handleElementHover(individual)}
                                            onMouseLeave={() => this.handleElementHover(null)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium truncate">
                                                    {individual.name}
                                                </div>
                                            </div>
                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/50 whitespace-nowrap">
                                                {individual.stereotype}
                                            </span>
                                        </div>
                                    ))
                                )}
                            </BrowserSection>

                            {/* Relationships Section */}
                            <BrowserSection
                                label="Relationships"
                                count={relationships.length}
                                panelExpanded={expanded}
                                icon={<RelationshipsIcon />}
                                iconColorClass="text-accent-teal dark:text-accent"
                                darkMode={this.props.darkMode}
                            >
                                {relationships.length === 0 ? (
                                    <div className="px-3 py-2 text-[11px] text-slate-400 dark:text-white/30 italic">
                                        No relationships yet
                                    </div>
                                ) : (
                                    relationships.map(relationship => (
                                        <div
                                            key={relationship.id}
                                            className={clsx(
                                                "flex items-center gap-2 px-2.5 py-1.5 mx-1.5 rounded-[8px] cursor-pointer transition-all duration-[120ms]",
                                                "hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10",
                                                isEdge(selectedElement) && selectedElement.id === relationship.id && "bg-accent-teal/15 dark:bg-accent/[0.18]"
                                            )}
                                            onClick={() => this.handleElementClick(relationship)}
                                            onMouseEnter={() => this.handleElementHover(relationship)}
                                            onMouseLeave={() => this.handleElementHover(null)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium truncate">
                                                    {relationship.name}
                                                </div>
                                                <div className="text-[10px] text-slate-400 dark:text-white/40 truncate">
                                                    {relationship.subject.name} → {relationship.object.name}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </BrowserSection>
                        </>
                    )}
                </div>

                {/* Name Editor for renaming elements */}
                {this.state.renameState.visible && this.state.renameState.element && (
                    <NameEditor
                        x={this.state.renameState.position.x}
                        y={this.state.renameState.position.y}
                        stereotype={
                            this.state.renameState.element instanceof Individual
                                ? (this.state.renameState.element.stereotype || this.state.renameState.element.modelClass?.label || '')
                                : ''
                        }
                        defaultName={this.state.renameState.element.name}
                        onConfirm={this.handleRenameConfirm}
                        onCancel={this.handleRenameCancel}
                        mode="rename"
                    />
                )}

                {/* Context Menu for SchemaIndividuals */}
                {this.state.contextMenu.visible && this.state.contextMenu.element && (
                    <ContextMenu
                        x={this.state.contextMenu.x}
                        y={this.state.contextMenu.y}
                        items={[
                            {
                                id: 'add-to-diagram',
                                label: 'Add to Diagram',
                                icon: (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                        <path d="M12 5v14M5 12h14" />
                                    </svg>
                                )
                            }
                        ]}
                        onSelect={this.handleContextMenuSelect}
                        onClose={this.hideContextMenu}
                        darkMode={this.props.darkMode}
                    />
                )}
            </div>
        );
    }
}
